import type * as React from 'react';

import {
  type DimeComboboxChipAction,
  DimeComboboxChip,
  DimeComboboxChips,
  DimeComboboxChipsInput,
  DimeComboboxValue,
} from '../DimeCombobox.js';
import type { DimeAutocompleteOption } from '../lib/dime-autocomplete-option.js';

type DimeAutocompleteChipsProps = Readonly<{
  anchorRef: React.Ref<HTMLDivElement>;
  id: string;
  selected: ReadonlyArray<DimeAutocompleteOption>;
  placeholder: string;
  chipRemoveLabel?: (label: string) => string;
  chipAction?: (option: DimeAutocompleteOption) => DimeComboboxChipAction | undefined;
  chipBadge?: (option: DimeAutocompleteOption) => string | undefined;
  inputRef?: React.Ref<HTMLInputElement>;
  maxLength?: number;
  disabled?: boolean;
  className?: string;
  onBlur?: () => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  'aria-invalid'?: boolean;
  'aria-required'?: boolean;
  'aria-describedby'?: string;
  'aria-busy'?: boolean;
  'aria-label'?: string;
}>;

/** A multi-select's picks as removable chips, with the search input after them; a disabled pick's chip can't be removed. */
export function DimeAutocompleteChips({
  anchorRef,
  id,
  selected,
  placeholder,
  chipRemoveLabel,
  chipAction,
  chipBadge,
  inputRef,
  disabled,
  className,
  ...inputProps
}: DimeAutocompleteChipsProps) {
  return (
    <DimeComboboxChips ref={anchorRef} className={className}>
      <DimeComboboxValue>
        {selected.map((option) => {
          const badge = chipBadge?.(option);
          return (
            <DimeComboboxChip
              key={option.value}
              removable={option.disabled !== true}
              removeLabel={chipRemoveLabel?.(option.label) ?? `Remove ${option.label}`}
              action={chipAction?.(option)}
            >
              {option.label}
              {badge ? <span className="ml-1 text-xs text-muted-foreground">{badge}</span> : null}
            </DimeComboboxChip>
          );
        })}
      </DimeComboboxValue>
      <DimeComboboxChipsInput
        id={id}
        ref={inputRef}
        disabled={disabled}
        placeholder={selected.length === 0 ? placeholder : undefined}
        {...inputProps}
      />
    </DimeComboboxChips>
  );
}
